/**
 * WebLLM Engine Adapter
 * Step 2: Implements EngineAdapter using @mlc-ai/web-llm
 * Step 4: Added progress callback support
 * Step 5: Added AsyncIterable streaming with ChatDeltaEvent
 */
import * as webllm from '@mlc-ai/web-llm';
/**
 * Adapter for the WebLLM inference engine.
 * Uses WebGPU for in-browser LLM inference.
 */
export class WebLLMAdapter {
    constructor(logger, config) {
        this.engine = null;
        this.loadedModelId = null;
        this.logger = logger;
        this.onProgress = config?.onProgress;
    }
    /**
     * Set or update the progress callback
     */
    setProgressCallback(callback) {
        this.onProgress = callback;
    }
    /**
     * Emit a progress event
     */
    emitProgress(phase, modelId, progress, message, warmStart) {
        if (this.onProgress) {
            this.onProgress({ phase, modelId, progress, message, warmStart });
        }
    }
    /**
     * Initialize the WebLLM engine.
     * Checks for WebGPU support and throws if unavailable.
     */
    async init() {
        this.logger.info('Initializing WebLLM adapter...');
        this.emitProgress('init', undefined, 0, 'Checking WebGPU support...');
        // Check WebGPU support
        if (!navigator.gpu) {
            const errorMsg = 'WebGPU is not supported in this browser. ' +
                'Please use a browser with WebGPU support (Chrome 113+, Edge 113+, or Safari 18+).';
            this.emitProgress('error', undefined, undefined, errorMsg);
            throw new Error(errorMsg);
        }
        // Try to request adapter to verify WebGPU is actually working
        try {
            const adapter = await navigator.gpu.requestAdapter();
            if (!adapter) {
                const errorMsg = 'Failed to get WebGPU adapter. Your GPU may not be supported.';
                this.emitProgress('error', undefined, undefined, errorMsg);
                throw new Error(errorMsg);
            }
            this.logger.debug('WebGPU adapter acquired', {
                vendor: adapter.info?.vendor,
                architecture: adapter.info?.architecture,
            });
        }
        catch (err) {
            const errorMsg = `WebGPU initialization failed: ${err instanceof Error ? err.message : String(err)}`;
            this.emitProgress('error', undefined, undefined, errorMsg);
            throw new Error(errorMsg);
        }
        this.logger.info('WebLLM adapter initialized (WebGPU available)');
        this.emitProgress('init', undefined, 100, 'WebGPU initialized');
    }
    /**
     * List available models.
     * Returns models from WebLLM's prebuilt list.
     */
    async listModels() {
        const prebuiltModels = webllm.prebuiltAppConfig.model_list;
        return prebuiltModels.map((model) => ({
            id: model.model_id,
            displayName: model.model_id,
            engine: 'webllm',
        }));
    }
    /**
     * Load a model into memory.
     * Downloads the model if not cached, then loads it.
     * @param modelId - Model ID to load
     * @param warmStart - Whether this is a warm start (model was previously loaded)
     */
    async loadModel(modelId, warmStart = false) {
        this.logger.info(`Loading model: ${modelId}`, { warmStart });
        // If already loaded with the same model, skip
        if (this.engine && this.loadedModelId === modelId) {
            this.logger.debug('Model already loaded in memory, skipping');
            this.emitProgress('ready', modelId, 100, 'Model ready (cached in memory)', true);
            return;
        }
        this.emitProgress('download', modelId, 0, warmStart ? 'Loading model (warm start)...' : 'Downloading model...', warmStart);
        try {
            // Create engine with progress callback
            this.engine = await webllm.CreateMLCEngine(modelId, {
                initProgressCallback: (progress) => {
                    // Normalize WebLLM progress into our format
                    const normalizedProgress = this.normalizeWebLLMProgress(progress, modelId, warmStart);
                    this.emitProgress(normalizedProgress.phase, modelId, normalizedProgress.progress, normalizedProgress.message, warmStart);
                    this.logger.debug('Model load progress', {
                        text: progress.text,
                        progress: progress.progress,
                    });
                },
            });
            this.loadedModelId = modelId;
            this.logger.info(`Model loaded: ${modelId}`);
            this.emitProgress('ready', modelId, 100, 'Model ready', warmStart);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            this.logger.error(`Failed to load model: ${message}`);
            this.emitProgress('error', modelId, undefined, `Failed to load model: ${message}`);
            throw new Error(`Failed to load model "${modelId}": ${message}`);
        }
    }
    /**
     * Normalize WebLLM's progress format into our SDK format.
     * WebLLM progress has: { text: string, progress: number }
     */
    normalizeWebLLMProgress(webllmProgress, modelId, warmStart) {
        const { text, progress } = webllmProgress;
        // Determine phase based on progress text
        // WebLLM typically emits messages like:
        // - "Loading model from cache..."
        // - "Fetching param cache[0/N]"
        // - "Loading GPU shader modules..."
        let phase = 'download';
        const lowerText = text.toLowerCase();
        if (lowerText.includes('shader') || lowerText.includes('gpu') || lowerText.includes('compil')) {
            phase = 'load';
        }
        else if (lowerText.includes('finish') || lowerText.includes('ready') || progress >= 1) {
            phase = 'ready';
        }
        // Convert progress from 0-1 to 0-100
        const progressPercent = Math.round(progress * 100);
        // Create user-friendly message
        let message = text;
        if (warmStart && phase === 'download') {
            message = `Verifying cached model... (${progressPercent}%)`;
        }
        return { phase, progress: progressPercent, message };
    }
    /**
     * Run chat inference (non-streaming).
     */
    async chat(request) {
        this.ensureEngineLoaded();
        const startTime = performance.now();
        this.logger.debug('Starting chat (non-streaming)', {
            model: request.model,
            messageCount: request.messages.length,
        });
        try {
            const response = await this.engine.chat.completions.create({
                messages: request.messages.map((m) => ({
                    role: m.role,
                    content: m.content,
                })),
                stream: false,
            });
            const durationMs = performance.now() - startTime;
            const content = response.choices[0]?.message?.content ?? '';
            const usage = this.extractUsage(response.usage);
            this.logger.info('Chat completed', { durationMs: durationMs.toFixed(2) });
            return {
                content,
                usage,
                meta: { durationMs },
            };
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            this.logger.error(`Chat failed: ${message}`);
            throw new Error(`Chat inference failed: ${message}`);
        }
    }
    /**
     * Run chat inference with streaming (Step 5).
     * Returns an AsyncIterable of ChatDeltaEvent objects.
     *
     * Normalization of WebLLM chunks:
     * - WebLLM emits chunks with `choices[0].delta.content` for text deltas
     * - Usage stats are included in the final chunk when `stream_options.include_usage` is true
     * - We normalize these into our ChatDeltaEvent format with 'delta', 'done', 'error' types
     */
    chatStream(request, requestId, warmStart) {
        const self = this;
        const logger = this.logger;
        const engine = this.engine;
        const modelId = request.model;
        const signal = request.signal;
        // Return an async generator
        async function* generateStream() {
            if (!engine) {
                yield {
                    type: 'error',
                    modelId,
                    requestId,
                    ts: Date.now(),
                    error: { message: 'No model loaded. Call loadModel(modelId) before chat().', code: 'NO_MODEL' },
                    meta: { engine: 'webllm', warmStart },
                };
                return;
            }
            logger.debug('Starting chat (AsyncIterable streaming)', {
                model: modelId,
                messageCount: request.messages.length,
                requestId,
            });
            try {
                // Check for abort before starting
                if (signal?.aborted) {
                    yield {
                        type: 'error',
                        modelId,
                        requestId,
                        ts: Date.now(),
                        error: { message: 'Request aborted before starting', code: 'ABORTED' },
                        meta: { engine: 'webllm', warmStart },
                    };
                    return;
                }
                const webllmStream = await engine.chat.completions.create({
                    messages: request.messages.map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                    stream: true,
                    stream_options: { include_usage: true },
                });
                let usage;
                for await (const chunk of webllmStream) {
                    // Check for abort during streaming
                    if (signal?.aborted) {
                        yield {
                            type: 'error',
                            modelId,
                            requestId,
                            ts: Date.now(),
                            error: { message: 'Request aborted', code: 'ABORTED' },
                            meta: { engine: 'webllm', warmStart },
                        };
                        return;
                    }
                    const deltaText = chunk.choices[0]?.delta?.content;
                    if (deltaText) {
                        yield {
                            type: 'delta',
                            deltaText,
                            modelId,
                            requestId,
                            ts: Date.now(),
                            meta: { engine: 'webllm', warmStart },
                        };
                    }
                    // Capture usage from final chunk
                    if (chunk.usage) {
                        usage = self.extractUsage(chunk.usage);
                    }
                }
                // Emit final 'done' event with usage
                yield {
                    type: 'done',
                    modelId,
                    requestId,
                    ts: Date.now(),
                    usage,
                    meta: { engine: 'webllm', warmStart },
                };
                logger.info('Streaming chat completed', { requestId });
            }
            catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                logger.error(`Streaming chat failed: ${message}`);
                yield {
                    type: 'error',
                    modelId,
                    requestId,
                    ts: Date.now(),
                    error: { message, code: 'INFERENCE_ERROR' },
                    meta: { engine: 'webllm', warmStart },
                };
            }
        }
        return generateStream();
    }
    /**
     * Run chat inference with callback-based streaming.
     * @deprecated Use chatStream() returning AsyncIterable instead.
     */
    async chatStreamCallback(request, onToken) {
        this.ensureEngineLoaded();
        const startTime = performance.now();
        this.logger.debug('Starting chat (callback streaming - deprecated)', {
            model: request.model,
            messageCount: request.messages.length,
        });
        let fullContent = '';
        try {
            const stream = await this.engine.chat.completions.create({
                messages: request.messages.map((m) => ({
                    role: m.role,
                    content: m.content,
                })),
                stream: true,
                stream_options: { include_usage: true },
            });
            let usage;
            for await (const chunk of stream) {
                // Check for abort
                if (request.signal?.aborted) {
                    throw new Error('Request aborted');
                }
                const delta = chunk.choices[0]?.delta?.content;
                if (delta) {
                    fullContent += delta;
                    onToken(delta);
                }
                // Capture usage from final chunk
                if (chunk.usage) {
                    usage = this.extractUsage(chunk.usage);
                }
            }
            const durationMs = performance.now() - startTime;
            this.logger.info('Streaming chat completed', {
                durationMs: durationMs.toFixed(2),
                contentLength: fullContent.length,
            });
            return {
                content: fullContent,
                usage,
                meta: { durationMs },
            };
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            this.logger.error(`Streaming chat failed: ${message}`);
            throw new Error(`Streaming chat inference failed: ${message}`);
        }
    }
    /**
     * Ensure the engine is loaded before inference.
     */
    ensureEngineLoaded() {
        if (!this.engine) {
            throw new Error('No model loaded. Call loadModel(modelId) before chat().');
        }
    }
    /**
     * Extract usage stats from WebLLM response.
     */
    extractUsage(usage) {
        if (!usage)
            return undefined;
        return {
            promptTokens: usage.prompt_tokens ?? 0,
            completionTokens: usage.completion_tokens ?? 0,
            totalTokens: usage.total_tokens ?? 0,
        };
    }
}
//# sourceMappingURL=WebLLMAdapter.js.map